import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export default function Login() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("smartsn_token", data.token);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.login.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/15 focus:border-[#FF7900] outline-none px-5 py-4 text-white placeholder:text-white/30 transition-colors duration-300";

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-[#111111] border border-white/10 p-10"
      >
        <div className="flex items-center gap-3 mb-10">
          <Logo size={40} />
          <span className="font-display font-semibold text-xl tracking-tight">
            Smart <span className="text-[#FF7900]">Telecom</span>{" "}Advisor
          </span>
        </div>
        <h1 className="font-display text-3xl font-medium mb-2">{t.login.title}</h1>
        <p className="text-sm text-white/50 mb-8">{t.login.sub}</p>

        <form onSubmit={submit} className="flex flex-col gap-5" data-testid="login-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.login.email}
            className={inputCls}
            data-testid="login-email-input"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.login.password}
            className={inputCls}
            data-testid="login-password-input"
          />
          {error && (
            <p className="text-sm text-red-400" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
          >
            {loading ? t.login.loading : t.login.submit}
          </button>
        </form>

        <Link
          to="/"
          data-testid="login-back-link"
          className="mt-8 inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#FF7900] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.login.back}
        </Link>
      </motion.div>
    </main>
  );
}
